<template>
    <div class="lg:hidden sticky top-0 z-40 bg-zinc-950/95 backdrop-blur-xl border-b border-white/[0.08]">
        <div class="px-4 pt-3 pb-2.5">
            <!-- Scrollable tab strip -->
            <div class="relative">
                <!-- Right fade gradient — hints that more tabs exist off-screen -->
                <div class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-zinc-950/95 to-transparent pointer-events-none z-10 rounded-r-[14px]"></div>

                <div class="flex gap-1 p-1 bg-white/[0.04] rounded-[14px] border border-white/[0.06] overflow-x-auto tabs-scroll">
                    <button
                        @click="$emit('updateCurrentView', 'all')"
                        :class="[
                            'flex-shrink-0 h-9 px-4 rounded-[11px] text-xs font-semibold transition-all',
                            currentView === 'all'
                                ? 'bg-white/[0.12] text-white shadow-sm scale-[0.98]'
                                : 'text-white/60 active:scale-95'
                        ]"
                    >
                        All
                    </button>

                    <button
                        @click="$emit('updateCurrentView', 'sponsored')"
                        :class="[
                            'flex-shrink-0 h-9 px-4 rounded-[11px] text-xs font-semibold transition-all flex items-center gap-1.5',
                            currentView === 'sponsored'
                                ? 'bg-white/[0.12] text-white shadow-sm scale-[0.98]'
                                : 'text-white/60 active:scale-95'
                        ]"
                    >
                        Sponsored
                        <span
                            v-if="sponsoredCount > 0"
                            class="min-w-[18px] px-1.5 h-[18px] rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center leading-none flex-shrink-0"
                        >
                            {{ sponsoredCount }}
                        </span>
                    </button>

                    <button
                        @click="$emit('updateCurrentView', 'released')"
                        :class="[
                            'flex-shrink-0 h-9 px-4 rounded-[11px] text-xs font-semibold transition-all flex items-center gap-1.5',
                            currentView === 'released'
                                ? 'bg-white/[0.12] text-white shadow-sm scale-[0.98]'
                                : 'text-white/60 active:scale-95'
                        ]"
                    >
                        Released
                        <span
                            v-if="releasedCount > 0"
                            class="min-w-[18px] px-1.5 h-[18px] rounded-full bg-green-600 text-white text-[10px] font-bold flex items-center justify-center leading-none flex-shrink-0"
                        >
                            {{ releasedCount }}
                        </span>
                    </button>

                    <button
                        v-if="isUserLoggedIn"
                        @click="$emit('updateCurrentView', 'mine')"
                        :class="[
                            'flex-shrink-0 h-9 px-4 rounded-[11px] text-xs font-semibold transition-all flex items-center gap-1.5',
                            currentView === 'mine'
                                ? 'bg-white/[0.12] text-white shadow-sm scale-[0.98]'
                                : 'text-white/60 active:scale-95'
                        ]"
                    >
                        Mine
                        <span
                            v-if="userSponsoredCount > 0"
                            class="min-w-[18px] px-1.5 h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center leading-none flex-shrink-0"
                        >
                            {{ userSponsoredCount }}
                        </span>
                    </button>
                </div>
            </div>

            <div v-if="currentView === 'all'" class="mt-2.5">
                <select
                    :value="currentSort"
                    @change="$emit('updateCurrentSort', $event.target.value)"
                    class="w-full h-9 bg-white/[0.04] border py-0 border-white/[0.06] text-white text-[13px] font-medium rounded-[11px] pl-3 pr-8 appearance-none focus:outline-none focus:ring-1 focus:ring-white/20 active:scale-[0.98] transition-all"
                    :style="{
                        backgroundImage: `url('data:image/svg+xml,%3Csvg width=%2710%27 height=%276%27 viewBox=%270 0 10 6%27 fill=%27none%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cpath d=%27M1 1L5 5L9 1%27 stroke=%27%23999%27 stroke-width=%271.5%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27/%3E%3C/svg%3E')`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center'
                    }"
                >
                    <option v-for="(label, value) in sortOptions" :value="value" :key="value">{{ label }}</option>
                </select>
            </div>
        </div>
    </div>
</template>

<script setup>
defineProps({
    currentView: {
        type: String,
    },
    currentSort: {
        type: String,
    },
    isUserLoggedIn: {
        type: Boolean,
    },
    userSponsoredCount: {
        type: Number,
    },
    releasedCount: {
        type: Number,
    },
    totalCount: {
        type: Number,
    },
    sponsoredCount: {
        type: Number,
        default: 0,
    },
    sortOptions: {
        type: Object,
    }
})

defineEmits(['updateCurrentView', 'updateCurrentSort'])
</script>

<style scoped>
/* Hide scrollbar across browsers while keeping scroll functionality */
.tabs-scroll {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
}
.tabs-scroll::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
}
</style>
